import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Spin,
  Card,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tabs
} from "antd";
import moment from "moment";
import styled from "styled-components";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/es/table";
import { TopbarExtension } from "components/topbarExtension";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import PaymentApp from "components/notificationsMenu/paymentApp";

interface LineItem {
  orderId: string;
  description: string;
  date: string;
  baseCost: number;
}

interface TaskCost {
  taskId: string;
  name: string;
  cost: number;
}

interface OrderDetail {
  orderId: string;
  orderName: string;
  taskCosts: TaskCost[];
  orderTotal: number;
}

interface FinalBill {
  grossTotal: number;
  subsidyAmount: number;
  insuranceAmount: number;
  finalPayable: number;
}

interface BillDetails {
  invoiceNumber: string;
  patientName: string;
  orders: OrderDetail[];
  finalBill: FinalBill;
}

interface FinancialProfile {
  meansTestTier: string;
  subsidyRate: number;
  insuranceCoverageRate: number;
}

interface BillingSummary {
  subtotal: number;
  governmentSubsidy: number;
  insuranceClaimable: number;
  netSelfPay: number;
}

interface PatientBillingData {
  patientId: string;
  patientName: string;
  financialProfile: FinancialProfile;
  lineItems: LineItem[];
  summary: BillingSummary;
}

const StyledCard = styled(Card)`
  margin: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const BillingHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 20px;
`;

const BillingTitle = styled.h1`
  font-family: Century Gothic Bold;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  color: #333;
`;

const PatientInfoWrapper = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 24px;
  padding: 16px 0;
  flex-wrap: wrap;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #333;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  z-index: 1000;
`;

const StyledTable = styled(Table)`
  .ant-table-tbody > tr > td {
    height: 52px;
    vertical-align: middle;
    text-align: center;
  }

  .ant-table-thead > tr > th {
    text-align: center;
    font-weight: bold;
  }

  .cost-cell {
    text-align: right;
    font-weight: 600;
  }
`;

const SummarySection = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 24px;
`;

const SummaryBox = styled(Card)`
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const SummaryTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 2px solid #f0f0f0;
    font-weight: bold;
  }
`;

const SummaryLabel = styled.span`
  color: #666;
  font-size: 12px;
`;

const SummaryValue = styled.span`
  font-weight: 600;
  font-size: 12px;
  color: #333;
`;

const NetPayValue = styled(SummaryValue)`
  color: #1890ff;
  font-size: 14px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const ServicesSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin: 20px 0 16px 0;
`;

const DetailsTable = styled(Table)`
  .ant-table-tbody > tr > td {
    height: 48px;
    vertical-align: middle;
    padding: 8px 12px;
    font-size: 12px;
  }

  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: bold;
    font-size: 12px;
    padding: 10px 12px;
  }

  .task-name {
    text-align: left;
    font-weight: 500;
  }

  .task-cost {
    text-align: right;
    font-weight: 600;
  }
`;

const BillingHistorySection = styled.div`
  margin-top: 40px;
  padding: 20px 0;
  border-top: 2px solid #f0f0f0;
`;

const BillingHistoryTitle = styled.h3`
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin: 0 0 16px 0;
`;

const HistoryTable = styled(Table)`
  .ant-table-tbody > tr > td {
    height: 48px;
    vertical-align: middle;
    padding: 8px 12px;
    font-size: 12px;
  }

  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: bold;
    font-size: 12px;
    padding: 10px 12px;
  }

  .paid-badge {
    color: #52c41a;
    font-weight: 600;
  }

  .action-link {
    color: #1890ff;
    cursor: pointer;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
const PaymentContainer = styled.div`
  width: 100%;
  max-width: 600px;
  height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: auto;
`;

const DetailsModalContent = styled.div`
  padding: 16px 0;
`;

const OrderSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background-color: #fafafa;
`;

const OrderTitle = styled.h4`
  font-size: 13px;
  font-weight: bold;
  color: #333;
  margin: 0 0 12px 0;
`;

const DetailsFooter = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 2px solid #f0f0f0;
`;

const FinalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;

  &:last-child {
    font-weight: bold;
    font-size: 14px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    color: #1890ff;
  }
`;

const FinalsLabel = styled.span`
  color: #666;
  font-weight: 600;
`;

const FinalsValue = styled.span`
  font-weight: 600;
`;

interface PaymentFormData {
  paymentMethod: string;
  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  cvv?: string;
}

// Generate mock data for different months
const generateMockBillingData = (
  month: number,
  year: number
): PatientBillingData => {
  const baseAmounts = [
    { orderId: "NGT-2026-001", description: "NG Tube Procedure", cost: 185 },
    {
      orderId: "EXT-XR-2026-002",
      description: "External Chest X-Ray & Transport",
      cost: 305
    },
    {
      orderId: "LAB-2026-003",
      description: "Laboratory Analysis",
      cost: 150
    }
  ];

  // Vary the data by month - show different services for different months
  const monthVariation = month % 3;
  const servicesForMonth = baseAmounts.slice(0, 2 + monthVariation);

  const lineItems: LineItem[] = servicesForMonth.map((item, idx) => ({
    orderId: `${item.orderId}-${month}`,
    description: item.description,
    date: `2026-${String(month).padStart(2, "0")}-${15 + idx * 5}`,
    baseCost: item.cost
  }));

  const subtotal = servicesForMonth.reduce((sum, item) => sum + item.cost, 0);
  const governmentSubsidy = Math.round(subtotal * 0.3 * 100) / 100;
  const insuranceClaimable = Math.round(subtotal * 0.5 * 100) / 100;
  const netSelfPay =
    Math.round((subtotal - governmentSubsidy - insuranceClaimable) * 100) / 100;

  return {
    patientId: "LTC-10245",
    patientName: "Mrs. Ophia Aahui",
    financialProfile: {
      meansTestTier: "Tier 3",
      subsidyRate: 0.3,
      insuranceCoverageRate: 0.5
    },
    lineItems,
    summary: {
      subtotal,
      governmentSubsidy,
      insuranceClaimable,
      netSelfPay
    }
  };
};

// Generate mock bill details for different months
const generateMockBillDetails = (month: number, year: number): BillDetails => {
  const monthVariation = month % 3;
  const orders: OrderDetail[] = [];

  // NGT is always included
  orders.push({
    orderId: `NGT-2026-001-${month}`,
    orderName: "NG Tube Procedure",
    taskCosts: [
      { taskId: "T-001", name: "Clinical Review", cost: 25.0 },
      { taskId: "T-002", name: "Consent", cost: 10.0 },
      { taskId: "T-003", name: "Consumables", cost: 45.0 },
      { taskId: "T-004", name: "Insertion", cost: 60.0 },
      { taskId: "T-005", name: "Monitoring", cost: 15.0 },
      { taskId: "T-006", name: "Feeding Setup", cost: 30.0 }
    ],
    orderTotal: 185.0
  });

  // EXT-XR is always included
  orders.push({
    orderId: `EXT-XR-2026-002-${month}`,
    orderName: "External Chest X-Ray & Transport",
    taskCosts: [
      { taskId: "T-101", name: "Ambulance Outbound", cost: 80.0 },
      { taskId: "T-102", name: "Escort", cost: 20.0 },
      { taskId: "T-103", name: "X-Ray Procedure", cost: 120.0 },
      { taskId: "T-104", name: "Ambulance Return", cost: 60.0 },
      { taskId: "T-105", name: "Results Review", cost: 25.0 }
    ],
    orderTotal: 305.0
  });

  // LAB is only included when monthVariation >= 1
  if (monthVariation >= 1) {
    orders.push({
      orderId: `LAB-2026-003-${month}`,
      orderName: "Laboratory Analysis",
      taskCosts: [
        { taskId: "T-201", name: "Blood Test", cost: 45.0 },
        { taskId: "T-202", name: "Urinalysis", cost: 30.0 },
        { taskId: "T-203", name: "Report Generation", cost: 20.0 },
        { taskId: "T-204", name: "Doctor Consultation", cost: 55.0 }
      ],
      orderTotal: 150.0
    });
  }

  const grossTotal = orders.reduce((sum, order) => sum + order.orderTotal, 0);
  const subsidyAmount = Math.round(grossTotal * 0.3 * 100) / 100;
  const insuranceAmount = Math.round(grossTotal * 0.5 * 100) / 100;
  const finalPayable =
    Math.round((grossTotal - subsidyAmount - insuranceAmount) * 100) / 100;

  return {
    invoiceNumber: `INV-2026-OP-${String(month).padStart(2, "0")}`,
    patientName: "Mrs. Ophia Aahui",
    orders,
    finalBill: {
      grossTotal,
      subsidyAmount,
      insuranceAmount,
      finalPayable
    }
  };
};

// Generate billing history data for current and previous months
const generateBillingHistory = () => {
  const billingHistory: Record<
    string,
    { billing: PatientBillingData; details: BillDetails }
  > = {};

  for (let i = 0; i < 6; i++) {
    let month = 3 - i; // April (3), March (2), February (1), January (0)
    let year = 2026;

    if (month <= 0) {
      month += 12;
      year -= 1;
    }

    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    billingHistory[monthKey] = {
      billing: generateMockBillingData(month, year),
      details: generateMockBillDetails(month, year)
    };
  }

  return billingHistory;
};

const PatientBilling: React.FC = () => {
  const [billingData, setBillingData] = useState<PatientBillingData | null>(
    null
  );
  const [billingHistory, setBillingHistory] = useState<
    Record<string, { billing: PatientBillingData; details: BillDetails }>
  >({});
  const [activeTab, setActiveTab] = useState<string>("current");
  const [loading, setLoading] = useState<boolean>(true);
  const [isPaymentModalVisible, setIsPaymentModalVisible] =
    useState<boolean>(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] =
    useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        // Generate billing history
        setTimeout(() => {
          const history = generateBillingHistory();
          setBillingHistory(history);
          setBillingData(history["2026-03"].billing);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error("Error fetching billing data:", error);
        message.error("Failed to load billing data");
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handlePaymentClick = () => {
    setIsPaymentModalVisible(true);
    form.resetFields();
  };

  const handleDetailsClick = (order: OrderDetail) => {
    setSelectedOrder(order);
    setIsDetailsModalVisible(true);
  };

  const handlePaymentSubmit = async (values: PaymentFormData) => {
    setIsProcessing(true);
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      message.success("Payment processed successfully!");
      setIsPaymentModalVisible(false);
      form.resetFields();
      // Optionally navigate back or refresh data
    } catch (error) {
      message.error("Payment failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const columns: ColumnsType<LineItem> = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: "12%"
    },
    {
      title: "Service Description",
      dataIndex: "description",
      key: "description",
      width: "40%"
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "18%",
      render: (date: string) => {
        const formattedDate = moment(date).isValid()
          ? moment(date).format("MMM DD, YYYY")
          : "-";
        return formattedDate;
      }
    },
    {
      title: "Cost (SGD)",
      dataIndex: "baseCost",
      key: "baseCost",
      width: "15%",
      render: (cost: number) => (
        <span className="cost-cell">${cost.toFixed(2)}</span>
      )
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "15%",
      render: (_: any, record: LineItem) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", fontWeight: 500 }}
          onClick={() => handleServiceViewClick(record)}
        >
          View Details
        </span>
      )
    }
  ];

  const handleTableRowClick = (lineItem: LineItem) => {
    const currentMonthDetails = billingHistory["2026-03"]?.details;
    if (!currentMonthDetails) return;

    const order = currentMonthDetails.orders.find(
      o => o.orderId === lineItem.orderId
    );
    if (order) {
      handleDetailsClick(order);
    }
  };

  const handleServiceViewClick = (lineItem: LineItem) => {
    const currentMonthDetails = billingHistory["2026-03"]?.details;
    if (!currentMonthDetails) return;

    const order = currentMonthDetails.orders.find(
      o => o.orderId === lineItem.orderId
    );
    if (order) {
      handleDetailsClick(order);
    }
  };

  const handleViewHistoryDetails = (monthKey: string) => {
    const historyBilling = billingHistory[monthKey]?.details;
    if (historyBilling && historyBilling.orders.length > 0) {
      handleDetailsClick(historyBilling.orders[0]);
    }
  };

  if (loading) {
    return (
      <LoadingWrapper>
        <Spin tip="Loading billing information..." />
      </LoadingWrapper>
    );
  }

  if (!billingData) {
    return (
      <LoadingWrapper>
        <p>No billing information available</p>
      </LoadingWrapper>
    );
  }

  return (
    <>
      {/* <TopbarExtension>
        <BillingHeaderWrapper>
          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <AppButton
              type={ButtonType.Secondary}
              onClick={handleBackClick}
              buttonContent="Back"
              style={{ width: "100px", marginTop: 0 }}
            />
            <BillingTitle>Monthly Bill</BillingTitle>
          </div>
        </BillingHeaderWrapper>
      </TopbarExtension> */}

      <StyledCard>
        {/* Billing Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "current",
              label: "Current Bill",
              children: null
            },
            {
              key: "history",
              label: "Billing History",
              children: null
            }
          ]}
          style={{ marginBottom: "24px" }}
        />

        {activeTab === "current" && (
          <>
            {/* Patient Information */}
            <PatientInfoWrapper>
              <InfoBox>
                <InfoLabel>Billing Cycle</InfoLabel>
                <InfoValue>April 2026 (Current)</InfoValue>
              </InfoBox>
              <InfoBox>
                <InfoLabel>Patient ID</InfoLabel>
                <InfoValue>{billingData.patientId}</InfoValue>
              </InfoBox>
              <InfoBox>
                <InfoLabel>Patient Name</InfoLabel>
                <InfoValue>{billingData.patientName}</InfoValue>
              </InfoBox>
              <InfoBox>
                <InfoLabel>Means Test Tier</InfoLabel>
                <InfoValue>
                  {billingData.financialProfile.meansTestTier}
                </InfoValue>
              </InfoBox>
              <InfoBox>
                <InfoLabel>Facility</InfoLabel>
                <InfoValue>KWSH LTC Facility</InfoValue>
              </InfoBox>
            </PatientInfoWrapper>

            {/* Services Rendered */}
            <ServicesSectionTitle>Services Rendered</ServicesSectionTitle>
            <StyledTable
              columns={columns as any}
              dataSource={billingData.lineItems}
              rowKey="orderId"
              pagination={false}
              onRow={(record: any) => ({
                onClick: () => handleTableRowClick(record),
                style: { cursor: "pointer" }
              })}
            />

            {/* Billing Summary */}
            <SummarySection>
              <SummaryBox>
                <SummaryTitle>Billing Summary</SummaryTitle>

                <SummaryRow>
                  <SummaryLabel>Subtotal</SummaryLabel>
                  <SummaryValue>
                    ${billingData.summary.subtotal.toFixed(2)}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>Government Subsidy (30%)</SummaryLabel>
                  <SummaryValue>
                    -${billingData.summary.governmentSubsidy.toFixed(2)}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>Insurance Claimable (50%)</SummaryLabel>
                  <SummaryValue>
                    -${billingData.summary.insuranceClaimable.toFixed(2)}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>Amount Due</SummaryLabel>
                  <NetPayValue>
                    ${billingData.summary.netSelfPay.toFixed(2)}
                  </NetPayValue>
                </SummaryRow>
              </SummaryBox>
            </SummarySection>

            {/* Action Buttons */}
            <ButtonWrapper>
              <AppButton
                type={ButtonType.Primary}
                onClick={() => setShowPaymentScreen(true)}
                buttonContent="Pay Now"
                style={{ width: "140px" }}
              />
              {/* <AppButton
                type={ButtonType.Secondary}
                onClick={() => window.print()}
                buttonContent="Print Invoice"
                style={{ width: "140px" }}
              /> */}
            </ButtonWrapper>
          </>
        )}

        {activeTab === "history" && (
          <div>
            {/* Billing History */}
            <BillingHistorySection
              style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}
            >
              <BillingHistoryTitle>Billing History</BillingHistoryTitle>
              <HistoryTable
                columns={
                  [
                    {
                      title: "Month",
                      dataIndex: "month",
                      key: "month",
                      width: "20%"
                    },
                    {
                      title: "Amount Due (SGD)",
                      dataIndex: "amountDue",
                      key: "amountDue",
                      width: "20%",
                      render: (amount: number) => `$${amount.toFixed(2)}`
                    },
                    {
                      title: "Services",
                      dataIndex: "services",
                      key: "services",
                      width: "35%"
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      width: "15%",
                      render: () => <span className="paid-badge">✓ Paid</span>
                    }
                    // {
                    //   title: "Action",
                    //   dataIndex: "action",
                    //   key: "action",
                    //   width: "10%",
                    //   render: (_: any, record: any) => (
                    //     <span
                    //       className="action-link"
                    //       onClick={() =>
                    //         handleViewHistoryDetails(record.monthKey)
                    //       }
                    //     >
                    //       View
                    //     </span>
                    //   )
                    // }
                  ] as ColumnsType<object>
                }
                dataSource={Object.keys(billingHistory)
                  .sort()
                  .reverse()
                  .filter(monthKey => monthKey !== "2026-03")
                  .map(monthKey => {
                    const [year, month] = monthKey.split("-");
                    const monthNames = [
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December"
                    ];
                    const monthName =
                      monthNames[parseInt(month) - 1] || "Unknown";
                    const billing = billingHistory[monthKey]?.billing;

                    return {
                      key: monthKey,
                      monthKey,
                      month: `${monthName} ${year}`,
                      amountDue: billing?.summary.netSelfPay || 0,
                      services: `${billing?.lineItems.length || 0} service(s)`,
                      status: "Paid"
                    };
                  })}
                pagination={false}
                size="small"
              />
            </BillingHistorySection>
          </div>
        )}
      </StyledCard>

      {/* Payment Modal */}
      <Modal
        title="Payment Information"
        open={isPaymentModalVisible}
        onCancel={() => setIsPaymentModalVisible(false)}
        footer={null}
        width={500}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePaymentSubmit}
          style={{ marginTop: "20px" }}
        >
          <Form.Item
            label="Payment Method"
            name="paymentMethod"
            rules={[
              { required: true, message: "Please select payment method" }
            ]}
          >
            <Select placeholder="Select payment method">
              <Select.Option value="credit_card">Credit Card</Select.Option>
              <Select.Option value="debit_card">Debit Card</Select.Option>
              <Select.Option value="bank_transfer">Bank Transfer</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Card Number"
            name="cardNumber"
            rules={[
              { required: true, message: "Please enter card number" },
              {
                pattern: /^\d{16}$/,
                message: "Please enter valid 16-digit card number"
              }
            ]}
          >
            <Input placeholder="1234 5678 9012 3456" maxLength={16} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Card Holder Name"
                name="cardHolder"
                rules={[
                  { required: true, message: "Please enter card holder name" }
                ]}
              >
                <Input placeholder="Full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Expiry Date"
                name="expiryDate"
                rules={[
                  { required: true, message: "Please enter expiry date" },
                  {
                    pattern: /^(0[1-9]|1[0-2])\/\d{2}$/,
                    message: "Format: MM/YY"
                  }
                ]}
              >
                <Input placeholder="MM/YY" maxLength={5} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="CVV"
            name="cvv"
            rules={[
              { required: true, message: "Please enter CVV" },
              { pattern: /^\d{3,4}$/, message: "Please enter valid CVV" }
            ]}
          >
            <Input placeholder="123" maxLength={4} type="password" />
          </Form.Item>

          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isProcessing}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "8px",
                fontSize: "14px"
              }}
            >
              {isProcessing
                ? "Processing..."
                : `Pay SGD $${billingData.summary.netSelfPay.toFixed(2)}`}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Bill Details Modal */}
      <Modal
        title={`Order Details - ${selectedOrder?.orderId}`}
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={null}
        width={700}
        centered
      >
        <DetailsModalContent>
          {selectedOrder && (
            <OrderSection>
              <OrderTitle>{selectedOrder.orderName}</OrderTitle>
              <DetailsTable
                columns={
                  [
                    {
                      title: "Task",
                      dataIndex: "name",
                      key: "name",
                      className: "task-name"
                    },
                    {
                      title: "Cost (SGD)",
                      dataIndex: "cost",
                      key: "cost",
                      className: "task-cost",
                      render: (cost: number) => `$${cost.toFixed(2)}`
                    }
                  ] as ColumnsType<object>
                }
                dataSource={selectedOrder.taskCosts}
                rowKey="taskId"
                pagination={false}
                size="small"
              />
              <div
                style={{
                  marginTop: "8px",
                  textAlign: "right",
                  fontWeight: "bold"
                }}
              >
                Order Total:{" "}
                <span style={{ color: "#1890ff" }}>
                  SGD ${selectedOrder.orderTotal.toFixed(2)}
                </span>
              </div>
            </OrderSection>
          )}
        </DetailsModalContent>
      </Modal>
      {showPaymentScreen && (
        <Overlay>
          <PaymentContainer>
            <PaymentApp onClose={() => setShowPaymentScreen(false)} />
          </PaymentContainer>
        </Overlay>
      )}
    </>
  );
};

export default PatientBilling;
